import { AMBER, INK, PAPER, TAU, clamp, disc, line, rounded, text } from './paint';
import { COLORS } from '../../shared/types';

/** Pale enamel, rather than emissive blobs: the ink silhouette does the hard work. */
export const LANTERN_COLORS = [
  '#f6da8a', '#b7dbc2', '#efbaa8', '#b9d7eb',
  '#d3c4e7', '#efc8dc', '#d5dfaa', '#f2c093',
] as const;
export const ENAMEL_COLORS = COLORS;

export interface LanternPose {
  x: number;
  y: number;
  color: number;
  number: number;
  facing: number;
  grounded: boolean;
  speed: number;
  vy: number;
  time: number;
  phase: number;
  reducedMotion: boolean;
  local?: boolean;
  disconnected?: boolean;
  landing?: number;
  anticipation?: number;
  celebration?: number;
  /** Authoritative supportId cue: a stiffened bail exposes the full contact surface. */
  carrying?: boolean;
  gameplay?: boolean;
  braced?: boolean;
  /** Hero illustration only. Actual couriers always use a 1:1 32 × 40 envelope. */
  scale?: number;
  /** Horizontal offset (px) of the identity badge alone: the fallen courier's badge shakes after a reset. */
  badgeShake?: number;
}

export function drawLantern(ctx: CanvasRenderingContext2D, pose: LanternPose): void {
  const still = pose.reducedMotion;
  // Input acknowledgement is semantic feedback, so it also remains visible with
  // reduced motion. Only its compression/expansion is suppressed in that preference.
  const request = clamp(pose.anticipation ?? 0, 0, 1);
  const running = pose.grounded && Math.abs(pose.speed) > 14;
  const step = still ? 0 : Math.sin(pose.phase * 1.4 + pose.time * 17);
  const steady = still || pose.carrying || request > 0;
  const bob = steady ? 0 : running ? Math.abs(step) * 0.55 : (Math.sin(pose.time * 2.3 + pose.phase) + 1) * 0.28;
  const landing = steady ? 0 : clamp(pose.landing ?? 0, 0, 1);
  const airborneStretch = steady || pose.grounded ? 0 : clamp(-pose.vy / 15000, -0.012, 0.022);
  const squash = landing * 0.082;
  const compression = still ? 0 : request * 0.12;
  const scale = pose.scale ?? 1;
  const color = (pose.gameplay ? ENAMEL_COLORS : LANTERN_COLORS)[((pose.color % 8) + 8) % 8];
  const facing = pose.facing < 0 ? -1 : 1;
  const happy = (pose.celebration ?? 0) > 0;

  ctx.save();
  ctx.translate(pose.x, pose.y);
  ctx.scale(scale, scale);
  ctx.globalAlpha *= pose.disconnected ? 0.36 : 1;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // Feet finish exactly at collision y + 40. The planted foot never bobs.
  const leftLift = running && step > 0 ? step * 1.6 : 0;
  const rightLift = running && step < 0 ? -step * 1.6 : 0;
  const footShift = running ? step * 1.1 : 0;
  rounded(ctx, 5.3 + footShift, 33.6 - leftLift, 8, 6.4, 2.4);
  ctx.fillStyle = INK;
  ctx.fill();
  rounded(ctx, 18.7 - footShift, 33.6 - rightLift, 8, 6.4, 2.4);
  ctx.fill();
  line(ctx, [7 + footShift, 38 - leftLift, 11.4 + footShift, 38 - leftLift], pose.braced ? '#efc465' : '#a0aa8e', 1.3);
  line(ctx, [20.5 - footShift, 38 - rightLift, 24.8 - footShift, 38 - rightLift], pose.braced ? '#efc465' : '#a0aa8e', 1.3);

  ctx.save();
  ctx.translate(16, 35.5);
  ctx.scale(1 + squash * 0.13 - airborneStretch * 0.45, 1 - squash + airborneStretch);
  ctx.translate(-16, -35.5 - bob);

  // The broad, flat-topped bail gives a stack a visible contact surface.
  ctx.beginPath();
  ctx.moveTo(9.1, 10.7 + compression * 27);
  ctx.lineTo(9.1, 6.7);
  ctx.quadraticCurveTo(9.1, 1.5, 13.4, 1.5);
  ctx.lineTo(18.6, 1.5);
  ctx.quadraticCurveTo(22.9, 1.5, 22.9, 6.7);
  ctx.lineTo(22.9, 10.7 + compression * 27);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.strokeStyle = request ? '#ffe397' : '#b4ad86';
  ctx.lineWidth = request ? 1.8 : 1;
  ctx.stroke();
  if (pose.carrying) {
    // A weighted carrying rail spans the actual rectangular head contact, including
    // off-centre stacks. It stays at y=0 rather than bobbing away from a friend's feet.
    line(ctx, [1.7, 1.7, 30.3, 1.7], INK, 3.4);
    line(ctx, [2.8, 1.2, 29.2, 1.2], request ? '#fff0ac' : '#e1cd91', request ? 1.6 : 1.1);
  }

  // Keep BOTH the carrying rail and feet fixed. The vessel compresses beneath the
  // handle, instead of turning the support plane off or squashing a rider's feet.
  ctx.save();
  ctx.translate(16, 35.5);
  ctx.scale(1, 1 - compression);
  ctx.translate(-16, -35.5);
  // Rounded enamel vessel, kept inside the authoritative 32 × 40 body.
  rounded(ctx, 1.1, 8.8, 29.8, 27.4, 8.6);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = pose.gameplay ? 2.6 : 2.2;
  ctx.stroke();

  if (request) {
    rounded(ctx, 3.6, 11.5, 24.8, 22.5, 6);
    ctx.fillStyle = `rgba(246,173,48,${request * 0.83})`;
    ctx.fill();
    ctx.strokeStyle = '#fff0b6';
    ctx.lineWidth = 1.8;
    ctx.stroke();
  }

  ctx.save();
  rounded(ctx, 2.3, 10, 27.4, 25, 7.5);
  ctx.clip();
  rounded(ctx, 4.5, 11.2, 5.1, 17.4, 3);
  ctx.fillStyle = pose.gameplay ? 'rgba(255,255,242,.22)' : 'rgba(255,255,242,.43)';
  ctx.fill();
  ctx.fillStyle = 'rgba(77,69,42,.10)';
  ctx.fillRect(2, 31.9, 29, 4);
  ctx.restore();
  line(ctx, [7, 10.8, 25, 10.8], 'rgba(41,63,57,.23)', 1);
  if (request) {
    line(ctx, [13, 14.5, 16, 12, 19, 14.5], INK, 2.5);
    line(ctx, [13, 14.5, 16, 12, 19, 14.5], '#fff7ca', 1.1);
  }

  // Little hands stay in the envelope, including the greeting pose.
  if (pose.carrying) {
    // Actual support is legible as a small two-handed lift, inside the same envelope.
    line(ctx, [4, 25, 2.7, 20, 3, 12.5], INK, 2.3);
    line(ctx, [28, 25, 29.3, 20, 29, 12.5], INK, 2.3);
    disc(ctx, 3.1, 12.2, 1.8, INK);
    disc(ctx, 28.9, 12.2, 1.8, INK);
  } else if (happy) {
    line(ctx, [3.5, 23, 1.7, 16.4], INK, 2.2);
    line(ctx, [28.5, 23, 30.3, 16.4], INK, 2.2);
  } else if (pose.braced) {
    line(ctx, [3.2, 24, 2.6, 28.5, 5.6, 29], INK, 2.1);
    line(ctx, [28.8, 24, 29.4, 28.5, 26.4, 29], INK, 2.1);
  } else {
    line(ctx, [2.4, 25, 3.6, 27 + (running ? step : 0)], INK, 1.8);
    line(ctx, [29.6, 25, 28.4, 27 - (running ? step : 0)], INK, 1.8);
  }

  const eyeShift = facing * 0.8;
  const blinkCycle = (pose.time + pose.phase * 0.7) % 4.7;
  const blink = !still && blinkCycle > 4.52;
  if (blink || happy) {
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.8;
    for (const x of [10.6, 21.2]) {
      ctx.beginPath();
      ctx.moveTo(x - 1.4 + eyeShift, 18.3);
      ctx.quadraticCurveTo(x + eyeShift, happy ? 16.2 : 18.9, x + 1.4 + eyeShift, 18.3);
      ctx.stroke();
    }
  } else {
    for (const x of [10.6, 21.2]) {
      rounded(ctx, x - 1.55 + eyeShift, 15.9, 3.1, pose.grounded ? 4.4 : 4.9, 1.55);
      ctx.fillStyle = INK;
      ctx.fill();
      disc(ctx, x + eyeShift + 0.25, 16.8, 0.5, PAPER);
    }
  }
  ctx.globalAlpha *= 0.38;
  disc(ctx, 6.8, 21.2, 1.7, '#c57469');
  disc(ctx, 25, 21.2, 1.7, '#c57469');
  ctx.globalAlpha /= 0.38;
  if (!pose.grounded && pose.vy < -100 && !still) {
    ctx.beginPath();
    ctx.ellipse(16.5 + eyeShift, 22.1, 1.1, 1.35, 0, 0, TAU);
    ctx.fillStyle = INK;
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(14 + eyeShift, 21.8);
    ctx.quadraticCurveTo(16.5 + eyeShift, 24.2, 19 + eyeShift, 21.8);
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.25;
    ctx.stroke();
  }

  // A persistent, high-contrast identity badge remains visible in a stack.
  const badgeX = 16 + (pose.badgeShake ?? 0);
  disc(ctx, badgeX, 29.8, 6.1, INK);
  ctx.beginPath();
  ctx.arc(badgeX, 29.8, 6.4, 0, TAU);
  ctx.strokeStyle = pose.local ? '#fff5c6' : 'rgba(255,251,225,.7)';
  ctx.lineWidth = pose.local ? 1.5 : 0.8;
  ctx.stroke();
  text(ctx, String(pose.number), badgeX, 30.3, 10.4, PAPER, 'center', 900);
  if (pose.local) {
    disc(ctx, 26.3, 11.5, 2, AMBER);
    ctx.strokeStyle = INK;
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
  ctx.restore();
  ctx.restore();

  if (pose.disconnected) {
    line(ctx, [12, 14, 20, 22, 12, 22, 20, 14], PAPER, 1.6);
  }
  ctx.restore();
}
